import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MessageService } from 'primeng/api';
import { HelpersService } from './helpers.service';
import { WorkshopJobcardComponent } from '../components/modals/workshop-jobcard/workshop-jobcard.component';
import { first } from 'rxjs';
import { SelectPrintTemplateModalComponent } from '../components/portal/bikeshop/receipt-template-designer/select-print-template-modal/select-print-template-modal.component';
import { ReceiptTemplateService } from '../components/portal/bikeshop/receipt-template-designer/services/receipt-template.service';

export interface ScheduleWorkshopServiceOptions {
  partnerId: string;
  partnerData: any;
  event?: any;
  slot?: any;
  selectedItem?: any;
  customer?: any;
  rentalBooking?: boolean;
  getNextAvailableTimeSlot?: (eventId: any, resourceId: any, dateStr: string, duration: number, ignoreLeave: boolean) => string;
  isSlotAvailable?: (eventId: any, resourceId: any, dateStr: string, duration: number) => boolean;
  openJobCard?: (jobcardId: any, defaultTab: string) => void;
  onRefreshEvents?: () => void;
  jobCardData?: (result: any) => any;
}

export interface OpenManageJobCardOptions {
  onRefreshEvents?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class NewJobcardService {

  constructor(
    private modalService: NgbModal,
    private messageService: MessageService,
    private helpersService: HelpersService,
    private receiptTemplateService: ReceiptTemplateService,
  ) { }

  /**
   * Opens the workshop jobcard component for creating a new service
   * @param options Configuration options for scheduling
   * @returns Promise that resolves when the modal is closed
   */
  scheduleWorkshopService(options: ScheduleWorkshopServiceOptions): Promise<any> {
    const modalRef = this.modalService.open(WorkshopJobcardComponent, {
      windowClass: 'workshop-jobcard-window',
      modalDialogClass: 'workshop-jobcard-modal-dialog',
      backdrop: 'static',
    });

    modalRef.componentInstance.partnerId = options.partnerId;

    if (options.event) {
      if (options.event.view.type === 'Week') {
        try {
          if (options.getNextAvailableTimeSlot && options.isSlotAvailable) {
            var lastServiceTime = options.getNextAvailableTimeSlot(
              options.event.id,
              options.event.resource.id,
              options.event.dateStr,
              180,
              options.isSlotAvailable(options.event.id, options.event.resource.id, options.event.dateStr, 60)
                ? false
                : true,
            );
            modalRef.componentInstance.selectedTimeSlot = lastServiceTime;
          } else {
            modalRef.componentInstance.selectedTimeSlot = options.event.dateStr;
          }
        } catch (err) {
          modalRef.componentInstance.selectedTimeSlot = options.event.dateStr;
        }
      } else if (options.event.view.type === 'TwoWeeks' && options.slot && options.slot.availableSlot) {
        modalRef.componentInstance.selectedTimeSlot = options.slot.availableSlot;
      } else {
        modalRef.componentInstance.selectedTimeSlot = options.event.dateStr;
      }
      modalRef.componentInstance.technician = {
        id: options.event.resource.id,
        name: options.event.resource.name,
      };
    }

    modalRef.componentInstance.fromCalendar = options.event ? true : false;
    modalRef.componentInstance.selectedItem = options.selectedItem;
    modalRef.componentInstance.customer = options.customer;
    modalRef.componentInstance.rentalBooking = options.rentalBooking ? true : false;

    return modalRef.result.then(
      (result) => {
        this.messageService.add({ life: 5000, detail: result.message });

        if (options.onRefreshEvents) {
          options.onRefreshEvents();
        }
        if (options.jobCardData) {
          options.jobCardData(result);
        }

        if (result.openBooking && result.jobcard && result.jobcard.ID && options.openJobCard) {
          options.openJobCard(
            result.jobcard.ID,
            options.partnerData.OpenTabOnScheduleAndOpen,
          );
        }

        setTimeout(() => {
          if (
            options.partnerData &&
            options.partnerData.OpenPrintOnNewJob &&
            result.jobcard.StatusID > 15 &&
            result.jobcard.StatusID < 50
          ) {
            this.printJobCardAutomatically(result);
          }
        }, 1000);

        return result;
      },
      (onrejected) => {
        if (options.onRefreshEvents) {
          options.onRefreshEvents();
        }
        throw onrejected;
      }
    );
  }

  printJobCard(result: any) {
    // Load the template cache then check for active JOB_CARD templates.
    this.receiptTemplateService.refresh().pipe(first()).subscribe(() => {
      this.receiptTemplateService.getActiveTemplates('JOB_CARD')
        //.pipe(first())
        .subscribe(activeTemplates => {
          console.log(activeTemplates);
          if (activeTemplates.length > 0) {
            this.openTemplatePrintModal(activeTemplates, result);
          } else {
            // No active receipt templates — fall back to the existing print path.
            this.openLegacyPrint(result);
          }
        });
    });
  }

  printJobCardAutomatically(result: any) {
    console.log(window.location.href);
    if (window.location.href.includes('receipt-templates')) {
      return;
    }
    console.log('Attempting to print jobcard with templates...');
    this.receiptTemplateService.refresh().pipe(first()).subscribe(() => {
      this.receiptTemplateService.getActiveTemplates('JOB_CARD')
        .pipe(first())
        .subscribe(activeTemplates => {
          console.log(window.location.href);
          console.log(activeTemplates);
          if (window.location.href.includes('receipt-templates')) {
            return;
          }
          if (activeTemplates.length > 0) {
            const defaultTemplate = activeTemplates.find(t => t.isGlobalDefault);
            var defaultTemplateId = '';
            if (defaultTemplate)
              defaultTemplateId = defaultTemplate.id;

            if (activeTemplates.length === 1) {
              const win = window.open(
                `/store/receipt-templates/print/${activeTemplates[0].id}/${result.jobcard.ID}`,
                '_blank',
              );
              if (win) win.focus();
            }
            else {
              if (defaultTemplateId !== '') {
                const win = window.open(
                  `/store/receipt-templates/print/${defaultTemplateId}/${result.jobcard.ID}`,
                  '_blank',
                );
                if (win) win.focus();
              }
              else {
                const modalRef = this.modalService.open(SelectPrintTemplateModalComponent, {
                  backdrop: true,
                  backdropClass: 'jobcard-modal-popup-backdrop',
                  windowClass: 'jobcard-modal-popup-window',
                  size: 'lg',
                });

                modalRef.componentInstance.templates = activeTemplates;

                modalRef.result.then(
                  (selectedTemplateId: string) => {
                    const win = window.open(
                      `/store/receipt-templates/print/${selectedTemplateId}/${result.jobcard.ID}`,
                      '_blank',
                    );
                    if (win) win.focus();
                  },
                  () => { /* dismissed — do nothing */ },
                );
              }
            }
          } else {
            // No active receipt templates — fall back to the existing print path.
            this.openLegacyPrint(result);
          }
        });
    });

  }

  private openTemplatePrintModal(activeTemplates: any[], result: any): void {

    if (window.location.href.includes('receipt-templates')) {
      return;
    }

    const modalRef = this.modalService.open(SelectPrintTemplateModalComponent, {
      backdrop: true,
      backdropClass: 'jobcard-modal-popup-backdrop',
      windowClass: 'jobcard-modal-popup-window',
      size: 'lg',
    });

    modalRef.componentInstance.templates = activeTemplates;

    modalRef.result.then(
      (selectedTemplateId: string) => {
        const win = window.open(
          `/store/receipt-templates/print/${selectedTemplateId}/${result.jobcard.ID}`,
          '_blank',
        );
        if (win) win.focus();
      },
      () => { /* dismissed — do nothing */ },
    );

  }

  private openLegacyPrint(result: any): void {
    if (this.helpersService.getPrinterType() === 'A4') {
      var win = window.open(`/print/${result.jobcard.ID}`, '_blank');
      win.focus();
    } else {
      var win = window.open(
        `/print-till-slip/${result.jobcard.ID}`,
        '_blank',
      );
      win.focus();
    }
  }



  /**
   * Opens the workshop jobcard component for managing an existing jobcard
   * @param jobcardID The ID of the jobcard to manage
   * @param options Configuration options for the modal
   * @returns Promise that resolves when the modal is closed
   */
  openManageJobCard(jobcardID: any, options?: OpenManageJobCardOptions, goToMessaging: boolean = false): Promise<any> {
    const modalRef = this.modalService.open(WorkshopJobcardComponent, {
      windowClass: 'workshop-jobcard-window',
      modalDialogClass: 'workshop-jobcard-modal-dialog',
      backdrop: 'static',
    });

    modalRef.componentInstance.jobCardID = jobcardID;
    modalRef.componentInstance.goToMessaging = goToMessaging;

    return modalRef.result.then(
      (result) => {
        if (options?.onRefreshEvents) {
          options.onRefreshEvents();
        }
        return result;
      },
      (onrejected) => {
        if (options?.onRefreshEvents) {
          options.onRefreshEvents();
        }
        throw onrejected;
      }
    );
  }
}
